import { generateMul2x1 } from '../generators/math/mul2x1';
import { generateMul2x2 } from '../generators/math/mul2x2';
import { generateDivRemainder } from '../generators/math/div-remainder';
import { loadBank, sampleBank } from '../content/bank-loader';
import type { GeneratedQuestion, Difficulty } from '../generators/math/types';
import type { Subject } from '../../shared/types';
import type { QuizPack } from './pdf-gen';

interface AnswerKeyEntry { idx: number; correct: string; }
export interface QuizPackWithKey extends QuizPack { answerKey: AnswerKeyEntry[]; }

export async function buildQuizPack(subject: Subject, kpId: string, count: number = 10): Promise<QuizPackWithKey> {
  const seed = Date.now() & 0xffff;
  const packId = generatePackId();
  const questions: GeneratedQuestion[] = [];

  if (kpId.startsWith('math.')) {
    // Use parametric generators
    const gen = pickMathGen(kpId);
    for (let i = 0; i < count; i++) {
      questions.push(gen(seed + i * 7, ((i % 3) + 1) as Difficulty));
    }
  } else {
    // Use bank sample
    const url = bankUrlForSubject(subject, kpId);
    if (!url) throw new Error(`No bank for ${subject}/${kpId}`);
    const bank = await loadBank(url);
    const sampled = sampleBank(bank, count);
    questions.push(...sampled);
  }

  const answerKey: AnswerKeyEntry[] = questions.map((q, idx) => ({
    idx: idx + 1,
    correct: q.kind === 'mcq'
      ? String.fromCharCode(65 + (q.correctAnswer as number))  // "A"/"B"/"C"/"D"
      : q.kind === 'fill'
        ? String(q.correctAnswer)
        : '',  // worked-example: no answer key entry
  }));

  return {
    packId, subject, kpId,
    questions, answerKey,
    generatedAtISO: new Date().toISOString(),
  };
}

function generatePackId(): string {
  return Math.random().toString(36).slice(2, 10).toUpperCase();
}

type MathGenerator = (seed: number, difficulty: Difficulty) => GeneratedQuestion;

function pickMathGen(kpId: string): MathGenerator {
  if (kpId === 'math.mul.2x1') return generateMul2x1;
  if (kpId === 'math.mul.2x2') return generateMul2x2;
  if (kpId === 'math.div.remainder') return generateDivRemainder;
  return generateMul2x1;  // fallback
}

function bankUrlForSubject(_subject: Subject, kpId: string): string | null {
  if (kpId.startsWith('chinese.')) return '/data/chinese-zhuyin-康軒.json';
  if (kpId.startsWith('english.')) return '/data/english-alphabet-翰林.json';
  if (kpId.startsWith('nature.')) return '/data/nature-water-states-南一.json';
  if (kpId.startsWith('social.')) return '/data/social-community-翰林.json';
  return null;
}
